import { supabase } from './supabase';
import type { Database, Tables } from './database.types';

type ActivityLog = Tables<'activity_log'>;
type ActivityLogInsert = Database['public']['Tables']['activity_log']['Insert'];

export interface ActivityWithUser extends ActivityLog {
  user: {
    id: string;
    name: string;
    email: string;
    avatar?: string;
  };
}

export interface LogActivityRequest {
  projectId: string;
  userId?: string; // Optional - will use current user if not provided
  activityType: ActivityType;
  targetType?: TargetType;
  targetId?: string;
  targetName?: string;
  details?: Record<string, any>;
}

export type ActivityType = 
  | 'project_created'
  | 'project_updated'
  | 'project_status_changed'
  | 'project_deadline_changed'
  | 'creative_uploaded'
  | 'analysis_completed'
  | 'version_created'
  | 'team_member_invited'
  | 'team_member_joined'
  | 'team_member_removed'
  | 'creative_deleted'
  | 'analysis_rerun';

export type TargetType = 
  | 'project'
  | 'creative'
  | 'analysis'
  | 'team_member'
  | 'version';

export class ActivityService {
  /**
   * Log a new activity for a project
   */
  static async logActivity(request: LogActivityRequest): Promise<void> {
    try {
      // Get current user if not provided
      let userId = request.userId;
      if (!userId) {
        const { data: { user }, error: userError } = await supabase.auth.getUser();
        if (userError || !user) {
          console.warn('No authenticated user for activity logging');
          return;
        }
        userId = user.id;
      }

      const activityData: ActivityLogInsert = {
        project_id: request.projectId,
        user_id: userId,
        activity_type: request.activityType,
        target_type: request.targetType || null,
        target_id: request.targetId || null,
        target_name: request.targetName || null,
        details: request.details || {}
      };

      const { error } = await supabase
        .from('activity_log')
        .insert(activityData);

      if (error) {
        console.error('Failed to log activity:', error);
        // Don't throw error - activity logging shouldn't break main functionality
      }
    } catch (error) {
      console.error('Activity logging error:', error);
      // Silently fail - activity logging is not critical
    }
  }

  /**
   * Get activities for a project with user information
   */
  static async getProjectActivities(
    projectId: string,
    limit: number = 50,
    offset: number = 0
  ): Promise<ActivityWithUser[]> {
    try {
      const { data: activities, error } = await supabase
        .from('activity_log')
        .select(`
          *,
          users!activity_log_user_id_fkey(id, name, email, avatar)
        `)
        .eq('project_id', projectId)
        .order('created_at', { ascending: false })
        .range(offset, offset + limit - 1);

      if (error) {
        throw new Error(`Failed to fetch activities: ${error.message}`);
      }

      return (activities || []).map(activity => ({
        ...activity,
        user: (activity.users as any) || {
          id: activity.user_id,
          name: 'Unknown User',
          email: 'unknown@example.com'
        }
      }));
    } catch (error) {
      console.error('Get project activities error:', error);
      throw error;
    }
  }

  /**
   * Get recent activities across all user's projects
   */
  static async getRecentActivities(
    limit: number = 20
  ): Promise<ActivityWithUser[]> {
    try {
      const { data: { user }, error: userError } = await supabase.auth.getUser();
      if (userError || !user) {
        throw new Error('User not authenticated');
      }

      // First get user's project IDs
      const { data: userProjects, error: projectsError } = await supabase
        .from('projects')
        .select('id')
        .eq('user_id', user.id);

      if (projectsError) {
        throw new Error(`Failed to fetch user projects: ${projectsError.message}`);
      }

      const projectIds = userProjects?.map(p => p.id) || [];
      
      if (projectIds.length === 0) {
        return []; // No projects, no activities
      }

      const { data: activities, error } = await supabase
        .from('activity_log')
        .select(`
          *,
          users!activity_log_user_id_fkey(id, name, email, avatar),
          projects!activity_log_project_id_fkey(id, name)
        `)
        .in('project_id', projectIds)
        .order('created_at', { ascending: false })
        .limit(limit);

      if (error) {
        throw new Error(`Failed to fetch recent activities: ${error.message}`);
      }

      return (activities || []).map(activity => ({
        ...activity,
        user: (activity.users as any) || {
          id: activity.user_id,
          name: 'Unknown User',
          email: 'unknown@example.com'
        }
      }));
    } catch (error) {
      console.error('Get recent activities error:', error);
      throw error;
    }
  }

  /**
   * Format activity message for display
   */
  static formatActivityMessage(activity: ActivityWithUser): string {
    const userName = activity.user.name || activity.user.email;
    const details = activity.details as Record<string, any> || {};

    switch (activity.activity_type) {
      case 'project_created':
        return `${userName} created the project`;
      
      case 'project_updated':
        return `${userName} updated project settings`;
      
      case 'project_status_changed':
        return `${userName} changed project status to ${details.newStatus || 'unknown'}`;
      
      case 'project_deadline_changed':
        if (details.oldDeadline && details.newDeadline) {
          return `${userName} updated project deadline from ${new Date(details.oldDeadline).toLocaleDateString()} to ${new Date(details.newDeadline).toLocaleDateString()}`;
        } else if (details.newDeadline) {
          return `${userName} set project deadline to ${new Date(details.newDeadline).toLocaleDateString()}`;
        } else if (details.oldDeadline) {
          return `${userName} removed project deadline`;
        }
        return `${userName} updated project deadline`;
      
      case 'creative_uploaded':
        return `${userName} uploaded creative "${activity.target_name || 'Unknown'}"`;
      
      case 'analysis_completed':
        const score = details.overallScore ? ` (Score: ${details.overallScore}%)` : '';
        return `Analysis completed for "${activity.target_name || 'Unknown'}"${score}`;
      
      case 'version_created':
        const version = details.versionNumber ? ` v${details.versionNumber}` : '';
        return `${userName} created${version} of "${activity.target_name || 'Unknown'}"`;
      
      case 'team_member_invited':
        return `${userName} invited ${details.email || 'someone'} to the project`;
      
      case 'team_member_joined':
        return `${userName} joined the project`;
      
      case 'team_member_removed':
        return `${userName} removed ${details.email || 'a member'} from the project`;
      
      case 'creative_deleted':
        return `${userName} deleted creative "${activity.target_name || 'Unknown'}"`;
      
      case 'analysis_rerun':
        return `${userName} re-analyzed "${activity.target_name || 'Unknown'}"`;
      
      default:
        return `${userName} performed ${activity.activity_type.replace(/_/g, ' ')}`;
    }
  }

  /**
   * Get activity icon based on activity type
   */
  static getActivityIcon(activityType: ActivityType): string {
    switch (activityType) {
      case 'project_created':
      case 'project_updated':
        return 'FolderPlus';
      
      case 'project_status_changed':
        return 'Settings';
      
      case 'project_deadline_changed':
        return 'Calendar';
      
      case 'creative_uploaded':
        return 'Upload';
      
      case 'analysis_completed':
      case 'analysis_rerun':
        return 'BarChart3';
      
      case 'version_created':
        return 'GitBranch';
      
      case 'team_member_invited':
      case 'team_member_joined':
        return 'UserPlus';
      
      case 'team_member_removed':
        return 'UserMinus';
      
      case 'creative_deleted':
        return 'Trash2';
      
      default:
        return 'Activity';
    }
  }

  /**
   * Get activity color based on activity type
   */
  static getActivityColor(activityType: ActivityType): string {
    switch (activityType) {
      case 'project_created':
      case 'creative_uploaded':
      case 'team_member_joined':
        return 'text-green-600 bg-green-100';
      
      case 'project_updated':
      case 'project_deadline_changed':
      case 'version_created':
        return 'text-blue-600 bg-blue-100';
      
      case 'analysis_completed':
      case 'analysis_rerun':
        return 'text-purple-600 bg-purple-100';
      
      case 'team_member_invited':
        return 'text-yellow-600 bg-yellow-100';
      
      case 'creative_deleted':
      case 'team_member_removed':
        return 'text-red-600 bg-red-100';
      
      default:
        return 'text-gray-600 bg-gray-100';
    }
  }

  /**
   * Helper methods for common activity logging scenarios
   */
  
  static async logProjectCreated(projectId: string, projectName: string): Promise<void> {
    await this.logActivity({
      projectId,
      activityType: 'project_created',
      targetType: 'project',
      targetId: projectId,
      targetName: projectName
    });
  }

  static async logCreativeUploaded(
    projectId: string, 
    creativeId: string, 
    creativeName: string
  ): Promise<void> {
    await this.logActivity({
      projectId,
      activityType: 'creative_uploaded',
      targetType: 'creative',
      targetId: creativeId,
      targetName: creativeName
    });
  }

  static async logAnalysisCompleted(
    projectId: string,
    creativeId: string,
    creativeName: string,
    overallScore?: number
  ): Promise<void> {
    await this.logActivity({
      projectId,
      activityType: 'analysis_completed',
      targetType: 'analysis',
      targetId: creativeId,
      targetName: creativeName,
      details: overallScore ? { overallScore } : {}
    });
  }

  static async logVersionCreated(
    projectId: string,
    creativeId: string,
    creativeName: string,
    versionNumber: number
  ): Promise<void> {
    await this.logActivity({
      projectId,
      activityType: 'version_created',
      targetType: 'version',
      targetId: creativeId,
      targetName: creativeName,
      details: { versionNumber }
    });
  }

  static async logProjectStatusChanged(
    projectId: string,
    projectName: string,
    newStatus: string
  ): Promise<void> {
    await this.logActivity({
      projectId,
      activityType: 'project_status_changed',
      targetType: 'project',
      targetId: projectId,
      targetName: projectName,
      details: { newStatus }
    });
  }

  static async logProjectDeadlineChanged(
    projectId: string,
    projectName: string,
    oldDeadline?: string | null,
    newDeadline?: string | null
  ): Promise<void> {
    await this.logActivity({
      projectId,
      activityType: 'project_deadline_changed',
      targetType: 'project',
      targetId: projectId,
      targetName: projectName,
      details: { oldDeadline, newDeadline }
    });
  }

  static async logCreativeDeleted(
    projectId: string,
    creativeId: string,
    creativeName: string
  ): Promise<void> {
    await this.logActivity({
      projectId,
      activityType: 'creative_deleted',
      targetType: 'creative',
      targetId: creativeId,
      targetName: creativeName
    });
  }

  // TEAM ANALYTICS EXTENSION
  /**
   * Get team activity summary for organization
   */
  static async getTeamActivitySummary(
    organizationId: string,
    days: number = 30
  ): Promise<{
    totalActivities: number;
    activitiesByType: Record<string, number>;
    activitiesByDay: Array<{ date: string; count: number }>;
    mostActiveMembers: Array<{ userId: string; name: string; count: number }>;
  }> {
    try {
      const { data: { user }, error: userError } = await supabase.auth.getUser();
      if (userError || !user) {
        throw new Error('User not authenticated');
      }

      const startDate = new Date();
      startDate.setDate(startDate.getDate() - days);

      // Get organization members first
      const { data: orgMembers, error: membersError } = await supabase
        .from('users')
        .select('id')
        .eq('organization_id', organizationId);

      if (membersError) {
        throw new Error(`Failed to fetch organization members: ${membersError.message}`);
      }

      const memberIds = orgMembers?.map(m => m.id) || [];

      if (memberIds.length === 0) {
        return {
          totalActivities: 0,
          activitiesByType: {},
          activitiesByDay: [],
          mostActiveMembers: []
        };
      }

      // Get organization projects
      const { data: orgProjects, error: projectsError } = await supabase
        .from('projects')
        .select('id')
        .in('user_id', memberIds);

      if (projectsError) {
        throw new Error(`Failed to fetch organization projects: ${projectsError.message}`);
      }

      const projectIds = orgProjects?.map(p => p.id) || [];

      if (projectIds.length === 0) {
        return {
          totalActivities: 0,
          activitiesByType: {},
          activitiesByDay: [],
          mostActiveMembers: []
        };
      }

      // Get activities with user info
      const { data: activities, error } = await supabase
        .from('activity_log')
        .select(`
          *,
          users!activity_log_user_id_fkey(id, name, email)
        `)
        .in('project_id', projectIds)
        .gte('created_at', startDate.toISOString())
        .order('created_at', { ascending: false });

      if (error) {
        throw new Error(`Failed to fetch team activities: ${error.message}`);
      }

      const activitiesData = activities || [];

      // Calculate summary metrics
      const totalActivities = activitiesData.length;

      // Activities by type
      const activitiesByType: Record<string, number> = {};
      activitiesData.forEach(activity => {
        activitiesByType[activity.activity_type] = (activitiesByType[activity.activity_type] || 0) + 1;
      });

      // Activities by day
      const activitiesByDay: Array<{ date: string; count: number }> = [];
      const dayMap = new Map<string, number>();

      activitiesData.forEach(activity => {
        if (activity.created_at) {
          const date = new Date(activity.created_at).toISOString().split('T')[0];
          dayMap.set(date, (dayMap.get(date) || 0) + 1);
        }
      });

      for (let i = 0; i < days; i++) {
        const date = new Date();
        date.setDate(date.getDate() - i);
        const dateStr = date.toISOString().split('T')[0];
        activitiesByDay.push({
          date: dateStr,
          count: dayMap.get(dateStr) || 0
        });
      }

      // Most active members
      const memberMap = new Map<string, { userId: string; name: string; count: number }>();

      activitiesData.forEach(activity => {
        const user = (activity.users as any);
        if (user) {
          const existing = memberMap.get(user.id);
          if (existing) {
            existing.count++;
          } else {
            memberMap.set(user.id, {
              userId: user.id,
              name: user.name || user.email,
              count: 1
            });
          }
        }
      });

      const mostActiveMembers = Array.from(memberMap.values())
        .sort((a, b) => b.count - a.count)
        .slice(0, 10);

      return {
        totalActivities,
        activitiesByType,
        activitiesByDay: activitiesByDay.reverse(),
        mostActiveMembers
      };
    } catch (error) {
      console.error('Get team activity summary error:', error);
      throw error;
    }
  }

  /**
   * Get member contributions analytics
   */
  static async getMemberContributions(
    organizationId: string,
    days: number = 30
  ): Promise<Array<{
    userId: string;
    name: string;
    email: string;
    avatar?: string;
    projectsContributed: number;
    creativesUploaded: number;
    analysesCompleted: number;
    totalActivities: number;
    lastActive: string;
  }>> {
    try {
      const { data: { user }, error: userError } = await supabase.auth.getUser();
      if (userError || !user) {
        throw new Error('User not authenticated');
      }

      const startDate = new Date();
      startDate.setDate(startDate.getDate() - days);

      // Get organization members
      const { data: members, error: membersError } = await supabase
        .from('users')
        .select('id, name, email, avatar')
        .eq('organization_id', organizationId);

      if (membersError) {
        throw new Error(`Failed to fetch organization members: ${membersError.message}`);
      }

      const contributionsData = await Promise.all(
        (members || []).map(async (member) => {
          // Get member's projects
          const { data: memberProjects } = await supabase
            .from('projects')
            .select('id')
            .eq('user_id', member.id);

          const projectIds = memberProjects?.map(p => p.id) || [];

          // Get member's activities
          const { data: activities } = await supabase
            .from('activity_log')
            .select('activity_type, created_at')
            .eq('user_id', member.id)
            .gte('created_at', startDate.toISOString());

          const activitiesData = activities || [];

          // Calculate metrics
          const projectsContributed = new Set(
            activitiesData.map(a => a.activity_type).filter(type => 
              ['project_created', 'project_updated'].includes(type)
            )
          ).size;

          const creativesUploaded = activitiesData.filter(a => 
            a.activity_type === 'creative_uploaded'
          ).length;

          const analysesCompleted = activitiesData.filter(a => 
            a.activity_type === 'analysis_completed'
          ).length;

          const lastActive = activitiesData.length > 0 
            ? activitiesData[0].created_at 
            : 'Never';

          return {
            userId: member.id,
            name: member.name || 'Unknown',
            email: member.email || 'unknown@example.com',
            avatar: member.avatar || undefined,
            projectsContributed: Math.max(projectsContributed, projectIds.length),
            creativesUploaded,
            analysesCompleted,
            totalActivities: activitiesData.length,
            lastActive: lastActive || 'Never'
          };
        })
      );

      return contributionsData.sort((a, b) => b.totalActivities - a.totalActivities);
    } catch (error) {
      console.error('Get member contributions error:', error);
      throw error;
    }
  }

  /**
   * Get team project statistics
   */
  static async getTeamProjectStats(
    organizationId: string
  ): Promise<{
    totalProjects: number;
    activeProjects: number;
    totalCreatives: number;
    totalAnalyses: number;
    avgProjectCompletion: number;
    projectsByStatus: Record<string, number>;
  }> {
    try {
      const { data: { user }, error: userError } = await supabase.auth.getUser();
      if (userError || !user) {
        throw new Error('User not authenticated');
      }

      // Get organization members
      const { data: members, error: membersError } = await supabase
        .from('users')
        .select('id')
        .eq('organization_id', organizationId);

      if (membersError) {
        throw new Error(`Failed to fetch organization members: ${membersError.message}`);
      }

      const memberIds = members?.map(m => m.id) || [];

      if (memberIds.length === 0) {
        return {
          totalProjects: 0,
          activeProjects: 0,
          totalCreatives: 0,
          totalAnalyses: 0,
          avgProjectCompletion: 0,
          projectsByStatus: {}
        };
      }

      // Get organization projects
      const { data: projects, error: projectsError } = await supabase
        .from('projects')
        .select('id, status')
        .in('user_id', memberIds);

      if (projectsError) {
        throw new Error(`Failed to fetch organization projects: ${projectsError.message}`);
      }

      const projectsData = projects || [];
      const projectIds = projectsData.map(p => p.id);

      // Get creatives count
      const { data: creatives } = await supabase
        .from('creatives')
        .select('id')
        .in('project_id', projectIds);

      // Get analyses count
      const { data: analyses } = await supabase
        .from('analyses')
        .select('id')
        .in('creative_id', (creatives || []).map(c => c.id));

      // Calculate metrics
      const totalProjects = projectsData.length;
      const activeProjects = projectsData.filter(p => p.status === 'active').length;
      const totalCreatives = creatives?.length || 0;
      const totalAnalyses = analyses?.length || 0;

      // Project completion (simplified: completed / total)
      const completedProjects = projectsData.filter(p => p.status === 'completed').length;
      const avgProjectCompletion = totalProjects > 0 ? (completedProjects / totalProjects) * 100 : 0;

      // Projects by status
      const projectsByStatus: Record<string, number> = {};
      projectsData.forEach(project => {
        const status = project.status || 'unknown';
        projectsByStatus[status] = (projectsByStatus[status] || 0) + 1;
      });

      return {
        totalProjects,
        activeProjects,
        totalCreatives,
        totalAnalyses,
        avgProjectCompletion,
        projectsByStatus
      };
    } catch (error) {
      console.error('Get team project stats error:', error);
      throw error;
    }
  }

  /**
   * Get team credit usage analytics
   */
  static async getTeamCreditUsage(
    organizationId: string,
    days: number = 30
  ): Promise<{
    totalCreditsUsed: number;
    creditsByMember: Array<{ userId: string; name: string; creditsUsed: number }>;
    creditsByPlatform: Record<string, number>;
    dailyUsage: Array<{ date: string; credits: number }>;
  }> {
    try {
      const { data: { user }, error: userError } = await supabase.auth.getUser();
      if (userError || !user) {
        throw new Error('User not authenticated');
      }

      const startDate = new Date();
      startDate.setDate(startDate.getDate() - days);

      // Get organization members
      const { data: members, error: membersError } = await supabase
        .from('users')
        .select('id, name, email')
        .eq('organization_id', organizationId);

      if (membersError) {
        throw new Error(`Failed to fetch organization members: ${membersError.message}`);
      }

      const memberIds = members?.map(m => m.id) || [];

      if (memberIds.length === 0) {
        return {
          totalCreditsUsed: 0,
          creditsByMember: [],
          creditsByPlatform: {},
          dailyUsage: []
        };
      }

      // Get creatives by organization members
      const { data: creatives, error: creativesError } = await supabase
        .from('creatives')
        .select('id, user_id')
        .in('user_id', memberIds);

      if (!creatives || creativesError) {
        return {
          totalCreditsUsed: 0,
          creditsByMember: [],
          creditsByPlatform: {},
          dailyUsage: []
        };
      }

      const creativeIds = creatives.map(c => c.id);

      // Get analyses (each analysis = 1 credit)
      const { data: analyses } = await supabase
        .from('analyses')
        .select('id, creative_id, platforms, created_at')
        .in('creative_id', creativeIds)
        .gte('created_at', startDate.toISOString());

      const analysesData = analyses || [];

      // Calculate total credits used
      const totalCreditsUsed = analysesData.length;

      // Credits by member
      const memberCreditsMap = new Map<string, number>();
      analysesData.forEach(analysis => {
        const creative = creatives.find(c => c.id === analysis.creative_id);
        if (creative && creative.user_id) {
          const userId = creative.user_id;
          memberCreditsMap.set(userId, (memberCreditsMap.get(userId) || 0) + 1);
        }
      });

      const creditsByMember = Array.from(memberCreditsMap.entries()).map(([userId, credits]) => {
        const member = members?.find(m => m.id === userId);
        return {
          userId,
          name: (member?.name ?? member?.email ?? 'Unknown'),
          creditsUsed: credits
        };
      }).sort((a, b) => b.creditsUsed - a.creditsUsed);

      // Credits by platform
      const creditsByPlatform: Record<string, number> = {};
      analysesData.forEach(analysis => {
        const platforms = analysis.platforms as string[] || [];
        platforms.forEach(platform => {
          creditsByPlatform[platform] = (creditsByPlatform[platform] || 0) + 1;
        });
      });

      // Daily usage
      const dailyUsage: Array<{ date: string; credits: number }> = [];
      const dayMap = new Map<string, number>();

      analysesData.forEach(analysis => {
        if (analysis.created_at) {
          const date = new Date(analysis.created_at).toISOString().split('T')[0];
          dayMap.set(date, (dayMap.get(date) || 0) + 1);
        }
      });

      for (let i = 0; i < days; i++) {
        const date = new Date();
        date.setDate(date.getDate() - i);
        const dateStr = date.toISOString().split('T')[0];
        dailyUsage.push({
          date: dateStr,
          credits: dayMap.get(dateStr) || 0
        });
      }

      return {
        totalCreditsUsed,
        creditsByMember,
        creditsByPlatform,
        dailyUsage: dailyUsage.reverse()
      };
    } catch (error) {
      console.error('Get team credit usage error:', error);
      throw error;
    }
  }
}

export default ActivityService; 